import { useLocation } from "react-router-dom";
import { Construction } from "lucide-react";

import { findItem, type NavItem, type Pillar, type PillarId } from "../app/navigation";
import { Card, PageHeader, cx } from "../components/ui";

/**
 * The honest screen for a feature whose API does not exist yet.
 *
 * It deliberately does not render a table of invented rows. A mock dashboard
 * screenshots well and is worth nothing: it has to be thrown away when the real
 * endpoint lands, and in the meantime it makes the product look further along
 * than it is. This states what is coming, in which phase, and why it is not here.
 */
export function PlannedPage({
  pillar,
  section,
}: {
  pillar: Pillar;
  section: PillarId;
}) {
  const { pathname } = useLocation();
  const active: NavItem | undefined = findItem(pathname);
  const phase = active?.phase ?? Math.min(...pillar.items.map((i) => i.phase));

  return (
    <>
      <PageHeader
        title={active?.label ?? pillar.label}
        subtitle={pillar.blurb}
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
            <Construction size={13} /> Phase {phase}
          </span>
        }
      />

      <Card className="mb-6 border-amber-200 bg-amber-50/40">
        <p className="text-sm text-slate-700">
          This screen is designed but not connected. The API for it does not exist
          yet, so there is nothing to show here — showing invented rows would be
          worse than showing nothing.
        </p>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        {pillar.items.map((item) => (
          <Card key={item.to} className={cx(item.to === pathname && "border-slate-400")}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <item.icon size={18} className="mt-0.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{item.label}</p>
                  {item.purpose ? (
                    <p className="mt-0.5 text-sm text-slate-500">{item.purpose}</p>
                  ) : null}
                </div>
              </div>
              <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {item.built ? "Live" : `Phase ${item.phase}`}
              </span>
            </div>
          </Card>
        ))}
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Section: {section}. Build order and exit criteria are in{" "}
        <code className="rounded bg-slate-100 px-1 py-0.5">docs/plan.md</code> §14.
      </p>
    </>
  );
}